import { resolveExtensionOfferLimitBounds } from "../../shared/extension-product-layout.js";
import {
  AI_RECOMMENDATION_GENERATION_MODE,
  getAiRecommendationMeta,
  isRuntimeAiCopyHydrated,
} from "../../shared/ai-recommendation-copy.js";
import { getStepSections } from "./popup-step-sections.js";

/** Set by hybrid.js on the first shell UPDATE_PROPS while runtime APIs are in flight. */
export const POPUP_RUNTIME_CONTENT_LOADING_KEY = "__runtimeContentLoading";

export function isPopupRuntimeContentLoading(popupData) {
  return popupData?.[POPUP_RUNTIME_CONTENT_LOADING_KEY] === true;
}

function forEachPopupSection(popupData, cb) {
  const schema = popupData?.schema;
  if (!Array.isArray(schema)) return;
  for (const step of schema) {
    const sections = getStepSections(step);
    for (let i = 0; i < sections.length; i++) {
      cb(sections[i], i, step);
    }
  }
}

function popupHasPendingHybridAiCopy(popupData) {
  if (!isHybridStorefrontRuntime()) return false;

  let pending = false;
  forEachPopupSection(popupData, (section) => {
    if (pending || section?.type !== "TEXT") return;
    if (section?.setting?.role !== "aiBanner" && !section?.aiMeta) return;

    const meta = getAiRecommendationMeta(section);
    if (meta.generationMode === AI_RECOMMENDATION_GENERATION_MODE.STATIC) return;
    if (!isRuntimeAiCopyHydrated(section)) {
      pending = true;
    }
  });
  return pending;
}

/** True when storefront runtime APIs finished and impression can be recorded. */
export function isPopupReadyForImpression(popupData) {
  if (!popupData) return false;
  if (isPopupRuntimeContentLoading(popupData)) return false;
  if (popupHasPendingHybridAiCopy(popupData)) return false;
  return true;
}

/** Storefront theme extension runtime — hybrid.js owns offer hydration and random API. */
export function isHybridStorefrontRuntime() {
  return (
    typeof window !== "undefined" &&
    window.isLiveModeTrigger === true
  );
}

/**
 * Live storefront: skip React-side offer fetch — hybrid.js hydrates via
 * window.weupsellStorefrontOfferResolution (GraphQL first, server fallback).
 */
export function shouldSkipReactRuntimeOfferFetch({
  isPreviewMode = false,
  embeddedInEditor = false,
} = {}) {
  return (
    isHybridStorefrontRuntime() &&
    !isPreviewMode &&
    !embeddedInEditor
  );
}

export function resolveRuntimeProductSlotCount(popupData, activeStep = 0, section = null) {
  const fromLimit = Number(
    popupData?.generalSettings?.condition?.product?.products_to_offer_limit
  );
  if (Number.isFinite(fromLimit) && fromLimit >= 1) {
    return Math.min(
      Math.round(fromLimit),
      resolveExtensionOfferLimitBounds(popupData).max
    );
  }
  const fromSection = Array.isArray(section?.products) ? section.products.length : 0;
  if (fromSection > 0) {
    return Math.min(fromSection, resolveExtensionOfferLimitBounds(popupData).max);
  }
  return resolveExtensionOfferLimitBounds(popupData).min;
}
